import NextStep from '../upgrade/NextStep';
import React, { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiDownload, FiRefreshCw } from 'react-icons/fi';
import { get, ref, update } from 'firebase/database';
import { db } from '../../firebaseConfig';
import { generateApplicationDocument, exportApplicationDocument, getSourceCvMarkdown } from '../applicationApi';
import MarkdownDocument, { savedDocumentMarkdown, markdownToHtml } from './MarkdownDocument';
import { documentPlainText, parseTextDocument } from './TextOnlyDocument';
import { safeExternalUrl } from './previewData';

const PREPARATION_TABS = [['cv', 'CV'], ['cover_letter', 'Cover Letter (optional)'], ['apply', 'Apply']];


export default function ApplicationStudio({ job, profile, user, documents, active = true, onNextOutreach }) {
  const studioId = useId();
  const [tab, setTab] = useState('cv');
  const [docs, setDocs] = useState(documents || {});
  const [content, setContent] = useState({ cv: '', cover_letter: '' });
  const [busy, setBusy] = useState('loading');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [preparationStarted, setPreparationStarted] = useState(false);
  const [sourceLoaded, setSourceLoaded] = useState(false);
  const mounted = useRef(true);
  const localKey = `jobseeqr:document-edits:${user?.uid || 'guest'}:${job.id}`;
  const localEdits = useRef({});
  const pendingEdits = useRef({});
  const saveTimer = useRef(null);
  const saveChain = useRef(Promise.resolve());
  const flushEditsRef = useRef(null);
  const flushEdits = () => {
    clearTimeout(saveTimer.current);
    const edits = pendingEdits.current;
    pendingEdits.current = {};
    if (!user?.uid || !Object.keys(edits).length) return saveChain.current;
    const changes = {};
    Object.entries(edits).forEach(([type, value]) => {
      const html = markdownToHtml(value);
      Object.entries({ contentFormat: 'markdown', editedMarkdown: value, editedHtml: html, editedText: documentPlainText(parseTextDocument(html)), editedAt: Date.now() }).forEach(([field, fieldValue]) => { changes[`documents/${type}/${field}`] = fieldValue; });
    });
    saveChain.current = saveChain.current.catch(() => {}).then(() => update(ref(db, `people/${user.uid}/application_index/applications/${job.id}`), changes));
    saveChain.current.catch(() => { if (mounted.current) setError('Couldn’t sync your edits. They are kept in this tab; download your PDF before leaving.'); });
    return saveChain.current;
  };
  flushEditsRef.current = flushEdits;
  useEffect(() => () => { void flushEditsRef.current?.().catch(() => {}); }, []);
  const editDocument = (value) => {
    setContent((current) => ({ ...current, [tab]: value }));
    localEdits.current = { ...localEdits.current, [tab]: value };
    pendingEdits.current[tab] = value;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { void flushEditsRef.current().catch(() => {}); }, 800);
    try { sessionStorage.setItem(localKey, JSON.stringify(localEdits.current)); } catch { /* Editing remains available. */ }
  };
  const hasCv = Boolean(profile?.cvMetadata?.storagePath);
  useEffect(() => {
    mounted.current = true;
    async function load() {
      try {
        const legacy = user?.uid ? await get(ref(db, `people/${user.uid}/application_documents/${job.id}`)) : null;
        if (!mounted.current) return;
        const saved = { ...(legacy?.val() || {}), ...documents };
        try { const edits = JSON.parse(sessionStorage.getItem(localKey) || '{}'); localEdits.current = Object.fromEntries(['cv', 'cover_letter'].filter((type) => typeof edits?.[type] === 'string').map((type) => [type, edits[type]])); } catch { localEdits.current = {}; }
        setDocs(saved);
        const existingType = ['cv', 'cover_letter'].find((type) =>
          Boolean(localEdits.current[type]?.trim()) ||
          (saved[type]?.tailoringMode !== 'uploaded_cv' && Boolean(savedDocumentMarkdown(saved[type], type)?.trim()))
        );
        setTab(existingType || 'apply');
        setSourceLoaded(Boolean(saved.cv) || Object.hasOwn(localEdits.current, 'cv'));
        setContent({ cv: savedDocumentMarkdown(saved.cv, 'cv'), cover_letter: savedDocumentMarkdown(saved.cover_letter, 'cover_letter'), ...localEdits.current });
      } catch { if (mounted.current) setError('Couldn’t load saved documents. Try reopening this job.'); }
      finally { if (mounted.current) setBusy(''); }
    }
    void load();
    return () => { mounted.current = false; };
    // The workspace remounts this editor when the job or signed-in user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const run = async (action, operation) => {
    setBusy(action); setError(''); setNotice('');
    try { await operation(); } catch (e) { if (mounted.current) setError(e.message || 'Something went wrong. Please try again.'); }
    finally { if (mounted.current) setBusy(''); }
  };
  const loadSource = () => {
    setSourceLoaded(true);
    return run('converting', async () => {
      const result = await getSourceCvMarkdown({ user, jobId: job.id });
      if (!mounted.current) return;
      setContent((current) => ({ ...current, cv: result.content }));
      setDocs((current) => ({ ...current, cv: { ...result, tailoringMode: 'uploaded_cv' } }));
    });
  };
  useEffect(() => {
    if (active && tab === 'cv' && hasCv && user && !busy && !sourceLoaded && !content.cv && !error) void loadSource();
    // Load the uploaded CV once, only when the preparation page becomes visible.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, tab, hasCv, user, busy, sourceLoaded, content.cv, error]);
  const hasContent = Boolean(content[tab]?.trim());
  const applyUrl = safeExternalUrl(job.applyUrl || job.applicationUrl || job.appLink || job.externalLink);
  const hasDocument = Boolean(docs[tab]) || Object.hasOwn(localEdits.current, tab) || hasContent;
  const hasPreparedDocument = ['cv', 'cover_letter'].some((type) =>
    Boolean(localEdits.current[type]?.trim()) ||
    (docs[type]?.tailoringMode !== 'uploaded_cv' && Boolean(content[type]?.trim()))
  );
  const showTabs = hasPreparedDocument || preparationStarted;
  const generate = (documentType = tab) => run('generating', async () => {
    setPreparationStarted(true);
    setTab(documentType);
    await flushEdits().catch(() => {});
    const result = await generateApplicationDocument({ user, jobId: job.id, documentType, job });
    if (!result.content?.trim()) throw new Error('No document was returned. Please try again.');
    if (!mounted.current) return;
    setDocs((current) => ({ ...current, [documentType]: result }));
    setContent((current) => ({ ...current, [documentType]: savedDocumentMarkdown(result, documentType) }));
    delete localEdits.current[documentType];
    setTab(documentType);
    try { sessionStorage.setItem(localKey, JSON.stringify(localEdits.current)); } catch { /* Generated documents are also stored by the service. */ }
    setNotice('');
  });
  const download = () => run('exporting', async () => {
    await flushEdits().catch(() => {});
    const result = await exportApplicationDocument({ user, jobId: job.id, documentType: tab, content: content[tab], contentFormat: 'markdown', job });
    const url = safeExternalUrl(result.downloadUrl);
    if (!url) throw new Error('The PDF could not be exported. Please try again.');
    if (!mounted.current) return;
    const anchor = document.createElement('a'); anchor.href = url; anchor.target = '_blank'; anchor.rel = 'noopener noreferrer'; anchor.download = `${tab}.pdf`; anchor.click();
    setNotice('PDF ready.');
  });
  const generateLabel = busy === 'generating' ? 'Generating…' : tab === 'cv' ? docs.cv?.tailoringMode === 'uploaded_cv' || !hasContent ? 'Tailor CV to this role' : 'Regenerate CV' : `${hasContent ? 'Regenerate' : 'Generate'} cover letter`;
  const mobileGenerateLabel = busy === 'generating' ? 'Generating…' : tab === 'cv' && (docs.cv?.tailoringMode === 'uploaded_cv' || !hasContent) ? 'Tailor CV' : hasContent ? 'Regenerate' : 'Generate';
  return <section className="opp-studio">
    <header className="opp-section-heading"><h2>Prepare your application</h2></header>
    {!hasCv && tab !== 'apply' && <p role="status">Upload your CV to generate documents for this role. <Link to="/profile">Go to your profile</Link></p>}
    {showTabs && <div className="opp-studio-toolbar">
      <div className="opp-tabs opp-document-tabs" role="tablist" aria-label="Application preparation">{PREPARATION_TABS.map(([value, label], index) => <button key={value} id={`${studioId}-${value}-tab`} type="button" role="tab" aria-label={label} aria-selected={tab === value} aria-controls={`${studioId}-panel`} tabIndex={tab === value ? 0 : -1} disabled={Boolean(busy)} onClick={() => setTab(value)} onKeyDown={(event) => {
        const direction = getComputedStyle(event.currentTarget).direction === 'rtl' ? -1 : 1;
        const next = event.key === 'ArrowRight' ? (index + direction + PREPARATION_TABS.length) % PREPARATION_TABS.length : event.key === 'ArrowLeft' ? (index - direction + PREPARATION_TABS.length) % PREPARATION_TABS.length : event.key === 'Home' ? 0 : event.key === 'End' ? PREPARATION_TABS.length - 1 : null;
        if (next === null) return;
        event.preventDefault(); setTab(PREPARATION_TABS[next][0]); event.currentTarget.parentElement.children[next].focus();
      }}><span className="opp-desktop-label" aria-hidden="true">{label}</span><span className="opp-mobile-label" aria-hidden="true">{value === 'cover_letter' ? 'Cover letter' : label}</span></button>)}</div>
      {tab !== 'apply' && <div className="opp-document-actions">
        <button type="button" className={`opp-button${hasContent ? ' is-secondary' : ''}`} aria-label={generateLabel} disabled={!hasCv || !user || Boolean(busy)} onClick={() => generate()}><FiRefreshCw aria-hidden="true" /><span className="opp-desktop-label" aria-hidden="true">{generateLabel}</span><span className="opp-mobile-label" aria-hidden="true">{mobileGenerateLabel}</span></button>
        <button type="button" className={`opp-button${hasContent ? '' : ' is-secondary'}`} disabled={!user || Boolean(busy) || !hasContent} onClick={download}><FiDownload aria-hidden="true" />{busy === 'exporting' ? 'Preparing PDF…' : 'Download PDF'}</button>
      </div>}
    </div>}
    <div id={`${studioId}-panel`} role={showTabs ? 'tabpanel' : undefined} aria-labelledby={showTabs ? `${studioId}-${tab}-tab` : undefined}>
      {tab === 'apply' ? <section className={`opp-panel opp-apply-panel${!hasPreparedDocument ? ' is-starting' : ''}`} aria-busy={Boolean(busy)}>
        <h3>Apply to {job.companyName}</h3>
        {applyUrl ? <><p>Complete your application on the company’s website.</p><a className="opp-button" href={applyUrl} target="_blank" rel="noopener noreferrer">Apply on company site <FiArrowUpRight aria-hidden="true" /></a></> : <p role="status">Application link unavailable.</p>}
        {!hasPreparedDocument && <div className="opp-apply-recommendation">
          <h3>Tailor your CV before you apply</h3>
          <p>Highlight the experience and skills relevant to this role.</p>
          <button type="button" className="opp-button is-secondary opp-tailor-start" disabled={!hasCv || !user || Boolean(busy)} onClick={() => generate('cv')}>
            {busy === 'generating' ? <><span className="opp-loading-spinner" />Tailoring your CV…</> : 'Tailor CV to this role'}
          </button>
          {!hasCv && <p><Link to="/profile">Upload your CV</Link> to get started.</p>}
        </div>}
        {error && <p role="alert">{error}</p>}
      </section> : <>
        {error && <div role="alert"><p>{error}</p>{tab === 'cv' && !preparationStarted && hasCv && !content.cv && <button className="opp-button is-secondary" disabled={Boolean(busy)} onClick={loadSource}>Retry CV conversion</button>}</div>}
        <div className="opp-document-surface" aria-busy={Boolean(busy)}>{['loading', 'converting', 'generating'].includes(busy) ? <div className="opp-document-empty" role="status"><span className="opp-loading-spinner" />{busy === 'generating' ? (tab === 'cv' ? 'Tailoring your CV to this role…' : 'Drafting your cover letter…') : busy === 'converting' ? 'Converting your uploaded CV…' : 'Loading your documents…'}</div> : !hasDocument ? <div className="opp-document-empty"><p>{tab === 'cv' ? (error ? 'Your CV could not be tailored. Try again using the button above.' : 'Your uploaded CV will appear here for editing and tailoring.') : 'Generate a cover letter using your uploaded CV and this job description.'}</p></div> : <MarkdownDocument key={tab} value={content[tab]} onChange={editDocument} type={tab} disabled={Boolean(busy)} />}</div>
        {notice && <p role="status">{notice}</p>}
      </>}
    </div>
    {hasPreparedDocument && onNextOutreach && <NextStep action="Find people to contact" onAction={onNextOutreach}>Your document is ready to review. I can help you find relevant people at {job.companyName || "this company"} next.</NextStep>}
  </section>;
}
